/**
 * Reduced-days calendar: when workdaysPerWeek < 5 the week table shows a
 * workday checkbox so a non-working weekday can be opted in without an absence.
 *
 * Assigned `work_weekdays` stay checked and locked. Extra weekdays start off and
 * may be checked; once checked they need Gesamt hours or an absence (F&E may be 0).
 *
 * Week completion (requiredCount / weekFullySaved / weekCompletion()) still
 * uses workdaysPerWeek — reminders, Live month stats and the admin matrix
 * must keep the same denominator. Do not count checked days instead.
 */

export type WorkdayCheckboxLockReason =
  | "holiday"
  | "future"
  | "outsideWindow"
  | "locked"
  | "absentElsewhere"
  | "otherProjectHours"
  | "assignedWorkday"
  | "hasData";

export function showsWorkdayColumn(workdaysPerWeek: number): boolean {
  return workdaysPerWeek < 5;
}

/** Saved / imported / cross-project days stay checked so existing data cannot hide. */
export function seedWorkdaySelected(
  saved: boolean,
  workWeekdays: readonly number[],
  weekday: number,
): boolean {
  return saved || workWeekdays.includes(weekday);
}

export function isAssignedWorkday(workWeekdays: readonly number[], weekday: number): boolean {
  return workWeekdays.includes(weekday);
}

/**
 * Assigned profile weekdays cannot be unchecked. Extra days can be toggled
 * unless a structural lock or already-tracked data applies.
 */
export function workdayCheckboxLockReason(
  row: {
    holiday: string | null;
    isFuture: boolean;
    isOutsideWindow: boolean;
    absentElsewhere: boolean;
    hoursSourceProjectNames: readonly string[];
    saved: boolean;
  },
  isMonthLocked: boolean,
  workWeekdays: readonly number[],
  weekday: number,
): WorkdayCheckboxLockReason | null {
  if (row.holiday) return "holiday";
  if (row.isFuture) return "future";
  if (row.isOutsideWindow) return "outsideWindow";
  if (isMonthLocked) return "locked";
  if (row.absentElsewhere) return "absentElsewhere";
  if (row.hoursSourceProjectNames.length > 0) return "otherProjectHours";
  if (isAssignedWorkday(workWeekdays, weekday)) return "assignedWorkday";
  if (row.saved) return "hasData";
  return null;
}

/**
 * Same exclusions as holiday / future / out-of-window / absent-elsewhere,
 * plus an unchecked workday when the checkbox column is shown.
 */
export function isWorkdayRowSavable(
  row: {
    holiday: string | null;
    isFuture: boolean;
    isOutsideWindow: boolean;
    absentElsewhere: boolean;
    isWorkdaySelected: boolean;
  },
  showColumn: boolean,
): boolean {
  if (row.holiday || row.isFuture || row.isOutsideWindow || row.absentElsewhere) {
    return false;
  }
  if (showColumn && !row.isWorkdaySelected) return false;
  return true;
}

const HOURS_EPSILON = 0.001;

/** Extra (not in work_weekdays) checked day with no Gesamt and no absence — save must ask for hours. */
export function extraCheckedDayMissingHours(
  row: { isWorkdaySelected: boolean; totalHours: number; absenceFraction: number },
  showColumn: boolean,
  workWeekdays: readonly number[],
  weekday: number,
): boolean {
  if (!showColumn || !row.isWorkdaySelected) return false;
  if (isAssignedWorkday(workWeekdays, weekday)) return false;
  if (row.absenceFraction > 0) return false;
  return row.totalHours <= HOURS_EPSILON;
}
